import { useEffect, useState } from "react";
import { gameName } from "@gierki/shared";
import { api } from "../api.ts";
import { useStore } from "../store.ts";
import { Ekran, Karta, Komunikat, Odznaka, Polaczenie, Pole, Przycisk } from "../ui.tsx";
import { NowaPartia } from "./NewGame.tsx";
import { Admin } from "./Admin.tsx";
import { Opcje } from "./Options.tsx";
import { Statystyki } from "./Stats.tsx";
import {
  disablePush,
  enablePush,
  fetchPushConfig,
  pushSupported,
  type PushConfig,
} from "../push.ts";

/**
 * Ekran główny: niedokończone partie i sposoby na rozpoczęcie nowej.
 *
 * Lista partii zastępuje pamiętanie kodów pokoi — przy czterech znanych kontach
 * zaproszenie z listy jest krótsze niż wpisywanie czterech cyfr. Kod pokoju
 * został jako opcja dla gościa, który konta jeszcze nie ma.
 */
export function Home() {
  const { user, games, status, joinGame, joinByCode, abandonGame, showToast } = useStore();
  const [ekran, setEkran] = useState<"lista" | "nowa" | "admin" | "opcje" | "staty">("lista");
  const [kod, setKod] = useState("");
  /*
   * Potwierdzenie kasowania trzymane przy konkretnej partii, nie jako
   * `window.confirm`: w zainstalowanej aplikacji systemowe okno wygląda jak
   * błąd przeglądarki, a drugie dotknięcie tego samego przycisku czyta się
   * jednoznacznie. Wybór gaśnie sam po chwili.
   */
  const [doUsuniecia, setDoUsuniecia] = useState<string | null>(null);

  useEffect(() => {
    if (!doUsuniecia) return;
    const timer = setTimeout(() => setDoUsuniecia(null), 4000);
    return () => clearTimeout(timer);
  }, [doUsuniecia]);

  const usun = async (gameId: string) => {
    try {
      await abandonGame(gameId);
      setDoUsuniecia(null);
    } catch (error) {
      showToast(error instanceof Error ? error.message : "Nie udało się usunąć partii.");
    }
  };

  const [nicki, setNicki] = useState<Record<string, string>>({});
  useEffect(() => {
    api
      .users()
      .then(({ users }) =>
        setNicki(Object.fromEntries(users.map((entry) => [entry.id, entry.nick]))),
      )
      .catch(() => undefined);
  }, []);

  const dolaczKodem = async () => {
    try {
      await joinByCode(kod.trim());
      setKod("");
    } catch (error) {
      showToast(error instanceof Error ? error.message : "Nie udało się dołączyć.");
    }
  };

  const wyloguj = async () => {
    await api.logout().catch(() => undefined);
    useStore.getState().disconnect();
    useStore.getState().setUser(null);
  };

  // Rozdzielnik ekranów stoi **pod** deklaracjami, bo ekran Opcji dostaje
  // `wyloguj` w propsie — nad nimi zmienna jest w martwej strefie i wejście
  // w Opcje wywracałoby aplikację.
  if (ekran === "nowa") return <NowaPartia onWroc={() => setEkran("lista")} />;
  if (ekran === "admin") return <Admin onWroc={() => setEkran("lista")} />;
  if (ekran === "staty") return <Statystyki onWroc={() => setEkran("opcje")} />;
  if (ekran === "opcje")
    return (
      <Opcje
        onWroc={() => setEkran("lista")}
        onKonta={user?.isAdmin ? () => setEkran("admin") : undefined}
        onStatystyki={() => setEkran("staty")}
        onWyloguj={() => void wyloguj()}
      />
    );

  return (
    <Ekran
      tytul={`Cześć, ${user?.nick ?? ""}`}
      podtytul="Twoje partie"
      /*
       * Jedno wejście w nagłówku, nie trzy.
       *
       * „Konta", „Opcje" i „Wyjdź" obok siebie zjadały całą szerokość paska
       * i z „Cześć, Michał" zostawało „Cz…". Nagłówek ma pokazywać, gdzie
       * jesteś; rzeczy robione raz na miesiąc siedzą w Opcjach.
       */
      akcja={
        <Przycisk wariant="cichy" onClick={() => setEkran("opcje")}>
          Opcje
        </Przycisk>
      }
      stopka={
        <>
          <Przycisk wariant="glowny" pelna onClick={() => setEkran("nowa")}>
            Nowa partia
          </Przycisk>
          <div className="flex gap-2">
            <Pole
              value={kod}
              onChange={(event) => setKod(event.target.value.replace(/\D/g, "").slice(0, 4))}
              placeholder="Kod pokoju"
              inputMode="numeric"
              className="tabular text-center text-2xl tracking-widest"
            />
            <Przycisk onClick={() => void dolaczKodem()} disabled={kod.length !== 4}>
              Dołącz
            </Przycisk>
          </div>
        </>
      }
    >
      <Polaczenie status={status} />

      <Powiadomienia />

      {games.length === 0 ? (
        <Komunikat>
          Nie masz żadnej niedokończonej partii. Załóż nową i zaproś resztę — pojawi się u nich na
          tym ekranie.
        </Komunikat>
      ) : null}

      {games.map((game) => (
        <Karta key={game.id} onClick={() => void joinGame(game.id)}>

          <div className="flex items-center justify-between gap-2">
            <div className="min-w-0">
              <div className="font-semibold">{gameName(game.gameType)}</div>
              <div className="text-sm text-tekst-slaby truncate">
                {game.players.map((player) => nicki[player.id] ?? player.nick).join(", ")}
              </div>
            </div>
            <div className="flex flex-col items-end gap-1 shrink-0">
              {game.status === "lobby" ? <Odznaka>Poczekalnia</Odznaka> : null}
              {game.awaitingYou ? <Odznaka aktywna>Gramy</Odznaka> : null}
              {game.code ? <span className="text-xs text-tekst-slaby tabular">kod {game.code}</span> : null}
              {/*
                * Kasowanie tylko dla hosta i tylko z potwierdzenia: partia
                * znika wszystkim, więc pomyłkowe dotknięcie kosztowałoby
                * cudzą rozgrywkę. Wiersz partii jest klikalny, stąd
                * `stopPropagation` — inaczej „Usuń" najpierw by do niej wchodziło.
                */}
              {game.hostId === user?.id ? (
                <button
                  type="button"
                  onClick={(event) => {
                    event.stopPropagation();
                    if (doUsuniecia === game.id) void usun(game.id);
                    else setDoUsuniecia(game.id);
                  }}
                  className={[
                    "min-h-10 rounded-xl px-3 text-sm",
                    doUsuniecia === game.id
                      ? "bg-blad text-tekst font-semibold"
                      : "text-tekst-slaby active:text-tekst",
                  ].join(" ")}
                >
                  {doUsuniecia === game.id ? "Na pewno?" : "Usuń"}
                </button>
              ) : null}
            </div>
          </div>
        </Karta>
      ))}
    </Ekran>
  );
}

/**
 * Przełącznik powiadomień.
 *
 * Pokazuje się tylko wtedy, gdy naprawdę da się je włączyć: przeglądarka to
 * potrafi, serwer ma klucze VAPID, a użytkownik nie zablokował ich wcześniej
 * na stałe. Przełącznik, który po kliknięciu nic nie robi, jest gorszy niż
 * jego brak.
 */
function Powiadomienia() {
  const [config, setConfig] = useState<PushConfig | null>(null);
  const [trwa, setTrwa] = useState(false);
  const showToast = useStore((state) => state.showToast);

  useEffect(() => {
    if (!pushSupported()) return;
    fetchPushConfig()
      .then(setConfig)
      .catch(() => undefined);
  }, []);

  if (!config?.enabled || !config.publicKey) return null;
  if (typeof Notification !== "undefined" && Notification.permission === "denied") {
    return (
      <Komunikat>
        Powiadomienia są zablokowane w ustawieniach przeglądarki. Można je włączyć w ustawieniach
        witryny.
      </Komunikat>
    );
  }

  const przelacz = async () => {
    setTrwa(true);
    try {
      if (config.subscribed) {
        await disablePush();
        setConfig({ ...config, subscribed: false });
      } else {
        const ok = await enablePush(config.publicKey as string);
        setConfig({ ...config, subscribed: ok });
        if (!ok) showToast("Nie udało się włączyć powiadomień.");
      }
    } finally {
      setTrwa(false);
    }
  };

  return (
    <Karta onClick={trwa ? undefined : () => void przelacz()}>
      <div className="flex items-center justify-between gap-3">
        <div>
          <div>Powiadomienia</div>
          <div className="text-sm text-tekst-slaby">Gdy zacznie się runda, a nie patrzysz w ekran</div>
        </div>
        <Odznaka aktywna={config.subscribed}>{config.subscribed ? "WŁ." : "WYŁ."}</Odznaka>
      </div>
    </Karta>
  );
}
